import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { FiArrowLeft, FiArrowRight, FiFileText, FiClock, FiCheck, FiEye, FiCalendar, FiFilter, FiX, FiEdit2, FiChevronDown, FiChevronUp } from 'react-icons/fi';
import SublyApi from '../../../helpers/Api';

// Helper function to format preview values intelligently
const formatPreviewValue = (value, key) => {
	// Handle null/undefined
	if (value === null || value === undefined) return null;

	// Handle empty strings
	if (value === '') return null;

	// Handle empty arrays
	if (Array.isArray(value)) {
		if (value.length === 0) return null;
		// For arrays of strings, join them
		const nonEmptyItems = value.filter(Boolean);
		if (nonEmptyItems.length === 0) return null;
		if (nonEmptyItems.every(v => typeof v === 'string')) {
			return nonEmptyItems.join(', ');
		}
		return `${nonEmptyItems.length} item${nonEmptyItems.length > 1 ? 's' : ''}`;
	}

	// Handle objects (like addresses)
	if (typeof value === 'object') {
		// Check if it's a file object - skip these
		if (value.url || value.filename) return null;

		// Check if address-like object
		if ('street' in value || 'city' in value || 'address' in value) {
			const parts = [
				value.street || value.address,
				value.city,
				value.state,
				value.zip || value.zipCode
			].filter(Boolean);
			return parts.length > 0 ? parts.join(', ') : null;
		}

		// Check if all values are empty
		const nonEmptyValues = Object.values(value).filter(v =>
			v !== '' && v !== null && v !== undefined &&
			!(Array.isArray(v) && v.length === 0)
		);
		if (nonEmptyValues.length === 0) return null;

		// For other objects, skip them (too complex for preview)
		return null;
	}

	// Handle booleans
	if (typeof value === 'boolean') {
		return value ? 'Yes' : 'No';
	}

	// Handle strings - trim whitespace
	const stringValue = String(value).trim();
	return stringValue.length > 0 ? stringValue : null;
};

const DRAFT_PREFIX = 'form_draft_';

/** Does a localStorage key hold one of THIS user's form drafts?
 *
 * FormRenderer is the only writer, and always writes `form_draft_${formId}_${username}`
 * (no other shape exists in git history). `formId` is the integer `forms.id`, so it
 * never contains `_`, while usernames may. So parse, don't pattern-match: strip the
 * prefix, split at the FIRST `_`, and require the remainder to equal the username
 * exactly (case-sensitive). "ann" must not see "anne"'s drafts, nor "smith"
 * "john_smith"'s — several accounts can now share one browser (#168). */
export const isDraftKeyForUser = (key, username) => {
	if (!username || typeof key !== 'string' || !key.startsWith(DRAFT_PREFIX)) return false;
	const rest = key.slice(DRAFT_PREFIX.length);
	const sep = rest.indexOf('_');
	if (sep <= 0) return false; // no form id
	return rest.slice(sep + 1) === username;
};

const FormSubmissionHistory = ({ onBack, onEdit }) => {
	const currentUser = useSelector((st) => st.user.currentUser);
	const token = useSelector((st) => st.user.token);

	// State management
	const [submissions, setSubmissions] = useState([]);
	const [drafts, setDrafts] = useState([]);
	const [combinedItems, setCombinedItems] = useState([]);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState('');
	const [filteredSubmissions, setFilteredSubmissions] = useState([]);
	const [statusFilter, setStatusFilter] = useState('all');
	const [formNameFilter, setFormNameFilter] = useState('all');

	// Fetch user submissions and drafts
	useEffect(() => {
		const fetchSubmissions = async () => {
			if (!currentUser || !token) return;

			setLoading(true);
			setError('');

			try {
				const response = await SublyApi.getFormSubmissions(currentUser.username, token);
				console.log('Submissions response:', response);
				
				if (response && response.submissions) {
					setSubmissions(response.submissions);
				} else {
					setSubmissions([]);
				}
			} catch (err) {
				console.error('Error fetching submissions:', err);
				setError('Failed to load submission history');
			} finally {
				setLoading(false);
			}
		};

		const fetchDrafts = () => {
			if (!currentUser) return;

			const draftsFromStorage = [];
			const username = currentUser.username;
			
			// Look for draft keys in localStorage
			Object.keys(localStorage).forEach(key => {
				if (isDraftKeyForUser(key, username)) {
					try {
						const draftData = JSON.parse(localStorage.getItem(key));
						if (draftData && draftData.formData && draftData.formName) {
							draftsFromStorage.push({
								id: `draft_${key}`,
								form_name: draftData.formName,
								form_type: 'draft', // We'll treat drafts as their own type
								status: 'draft',
								created_at: draftData.timestamp,
								updated_at: draftData.timestamp,
								data: draftData.formData,
								form_id: draftData.formId,
								isDraft: true
							});
						}
					} catch (e) {
						console.error('Error parsing draft:', e);
					}
				}
			});

			setDrafts(draftsFromStorage);
		};

		fetchSubmissions();
		fetchDrafts();
	}, [currentUser, token]);

	// Combine submissions and drafts
	useEffect(() => {
		const combined = [...submissions, ...drafts];
		setCombinedItems(combined);
	}, [submissions, drafts]);

	// Filter combined items based on selected filters
	useEffect(() => {
		let filtered = [...combinedItems];

		if (statusFilter !== 'all') {
			if (statusFilter === 'in-progress') {
				// Filter for both 'in-review' and 'tasked' statuses
				filtered = filtered.filter(item =>
					item.status === 'in-review' || item.status === 'tasked'
				);
			} else {
				filtered = filtered.filter(item => item.status === statusFilter);
			}
		}

		if (formNameFilter !== 'all') {
			filtered = filtered.filter(item => item.form_name === formNameFilter);
		}

		// Sort by submitted_at descending (newest first)
		filtered.sort((a, b) => new Date(b.submitted_at || b.created_at) - new Date(a.submitted_at || a.created_at));

		setFilteredSubmissions(filtered);
	}, [combinedItems, statusFilter, formNameFilter]);

	// Get user-friendly status display info
	const getStatusInfo = (status) => {
		switch (status) {
			case 'submitted':
				return {
					icon: FiClock,
					color: 'text-blue-600',
					bgColor: 'bg-blue-100',
					label: 'Submitted'
				};
			case 'in-review':
			case 'tasked':
				// Combine admin statuses into user-friendly "In Progress"
				return {
					icon: FiEye,
					color: 'text-green-600',
					bgColor: 'bg-green-100',
					label: 'Processed'
				};
			case 'draft':
				return {
					icon: FiEdit2,
					color: 'text-gray-600',
					bgColor: 'bg-gray-100',
					label: 'Draft'
				};
			default:
				return {
					icon: FiFileText,
					color: 'text-gray-600',
					bgColor: 'bg-gray-100',
					label: status || 'Unknown'
				};
		}
	};

	// Format date for display
	const formatDate = (dateString) => {
		if (!dateString) {
			return 'No date available';
		}

		const date = new Date(dateString);

		// Check if the date is valid
		if (isNaN(date.getTime())) {
			return 'Invalid date';
		}

		return date.toLocaleDateString('en-US', {
			year: 'numeric',
			month: 'short',
			day: 'numeric',
			hour: '2-digit',
			minute: '2-digit'
		});
	};

	// Get preview of first few form entries
	const getFormDataPreview = (submission, maxFields = 3) => {
		if (!submission.data) return [];

		try {
			const data = typeof submission.data === 'string'
				? JSON.parse(submission.data)
				: submission.data;

			const entries = Object.entries(data)
				.map(([key, value]) => {
					// Skip metadata fields
					if (key.startsWith('_') || key === 'id' || key === 'formId') return null;

					// Use the smart formatter
					const formattedValue = formatPreviewValue(value, key);
					if (!formattedValue) return null;

					return {
						label: key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
						value: formattedValue.length > 60 ? formattedValue.substring(0, 60) + '...' : formattedValue
					};
				})
				.filter(Boolean) // Remove null entries
				.slice(0, maxFields);

			return entries;
		} catch (e) {
			console.error('Error parsing form data:', e);
			return [];
		}
	};

	// Get unique statuses and form names for filters
	const getFilterOptions = () => {
		const rawStatuses = [...new Set(combinedItems.map(s => s.status))];
		// Map to user-friendly status options
		const statusOptions = [];
		if (rawStatuses.includes('submitted')) {
			statusOptions.push({ value: 'submitted', label: 'Submitted' });
		}
		if (rawStatuses.includes('in-review') || rawStatuses.includes('tasked')) {
			statusOptions.push({ value: 'in-progress', label: 'Processed' });
		}
		if (rawStatuses.includes('draft')) {
			statusOptions.push({ value: 'draft', label: 'Draft' });
		}

		const formNames = [...new Set(combinedItems.map(s => s.form_name))].sort();
		return { statusOptions, formNames };
	};

	const { statusOptions, formNames } = getFilterOptions();

	if (loading) {
		return (
			<div className='min-h-screen bg-gray-50 p-4 sm:p-6 lg:p-8'>
				<div className='max-w-4xl mx-auto'>
					<div className='flex items-center justify-center h-64'>
						<div className='text-center'>
							<div className='w-16 h-16 border-4 border-dashed rounded-full animate-spin border-primary'></div>
							<h2 className='mt-4 font-glacial text-xl text-gray-700'>
								Loading Submissions...
							</h2>
						</div>
					</div>
				</div>
			</div>
		);
	}

	if (error) {
		return (
			<div className='min-h-screen bg-gray-50 p-4 sm:p-6 lg:p-8'>
				<div className='max-w-4xl mx-auto'>
					<div className='bg-white rounded-lg shadow-sm border p-8 text-center'>
						<div className='text-red-500 mb-4'>
							<FiFileText className='mx-auto h-12 w-12' />
						</div>
						<h2 className='text-xl font-glacial text-gray-900 mb-2'>
							Unable to Load Submissions
						</h2>
						<p className='text-sm font-montserrat-regular text-gray-600 mb-6'>
							{error}
						</p>
						<button
							onClick={onBack}
							className='inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-montserrat-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-900'>
							<FiArrowLeft className='mr-2 h-4 w-4' />
							Back to Forms
						</button>
					</div>
				</div>
			</div>
		);
	}

	return (
		<div className='min-h-screen bg-gray-50 p-4 sm:p-6 lg:p-8'>
			<div className='max-w-6xl mx-auto'>
				{/* Header */}
				<div className='mb-8'>
					<div className='flex items-center justify-between'>
						<div>
							<button
								onClick={onBack}
								className='inline-flex items-center text-sm font-montserrat-medium text-gray-600 hover:text-gray-900 mb-4 transition-colors'>
								<FiArrowLeft className='mr-2 h-4 w-4' />
								Back to Forms
							</button>
							<h1 className='text-3xl font-glacial font-bold text-gray-900 tracking-tight'>
								Submission History
							</h1>
							<p className='mt-2 text-sm font-montserrat-regular text-gray-600'>
								View and track all your form submissions
							</p>
						</div>
					</div>
				</div>

				{/* Filters */}
				{submissions.length > 0 && (
					<div className='bg-white rounded-lg shadow-sm border p-6 mb-6'>
						<div className='flex items-center gap-4'>
							<FiFilter className='h-5 w-5 text-gray-400' />
							<div className='flex flex-wrap gap-4'>
								{/* Status Filter */}
								<div>
									<label className='block text-xs font-montserrat-medium text-gray-600 mb-1'>
										Status
									</label>
									<select
										value={statusFilter}
										onChange={(e) => setStatusFilter(e.target.value)}
										className='text-sm font-montserrat-regular border border-gray-300 rounded-md px-3 py-1 focus:outline-none focus:ring-1 focus:ring-gray-900 focus:border-gray-900'>
										<option value='all'>All Statuses</option>
										{statusOptions.map(option => (
											<option key={option.value} value={option.value}>
												{option.label}
											</option>
										))}
									</select>
								</div>

								{/* Form Name Filter */}
								<div>
									<label className='block text-xs font-montserrat-medium text-gray-600 mb-1'>
										Form
									</label>
									<select
										value={formNameFilter}
										onChange={(e) => setFormNameFilter(e.target.value)}
										className='text-sm font-montserrat-regular border border-gray-300 rounded-md px-3 py-1 focus:outline-none focus:ring-1 focus:ring-gray-900 focus:border-gray-900'>
										<option value='all'>All Forms</option>
										{formNames.map(name => (
											<option key={name} value={name}>
												{name}
											</option>
										))}
									</select>
								</div>
							</div>
						</div>
					</div>
				)}

				{/* Active Filter Chips */}
				{(statusFilter !== 'all' || formNameFilter !== 'all') && (
					<div className='bg-white rounded-lg shadow-sm border p-4 mb-6'>
						<div className='flex items-center justify-between'>
							<div className='flex items-center gap-2 flex-wrap'>
								<span className='text-xs font-montserrat-medium text-gray-600'>Active filters:</span>
								{statusFilter !== 'all' && (
									<span className='inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-montserrat-medium bg-gray-100 text-gray-800'>
										Status: {statusOptions.find(opt => opt.value === statusFilter)?.label || statusFilter}
										<button
											onClick={() => setStatusFilter('all')}
											className='ml-1 text-gray-400 hover:text-gray-600'>
											<FiX className='h-3 w-3' />
										</button>
									</span>
								)}
								{formNameFilter !== 'all' && (
									<span className='inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-montserrat-medium bg-gray-100 text-gray-800'>
										Form: {formNameFilter}
										<button
											onClick={() => setFormNameFilter('all')}
											className='ml-1 text-gray-400 hover:text-gray-600'>
											<FiX className='h-3 w-3' />
										</button>
									</span>
								)}
							</div>
							<button
								onClick={() => {
									setStatusFilter('all');
									setFormNameFilter('all');
								}}
								className='text-xs font-montserrat-medium text-gray-600 hover:text-gray-900'>
								Clear all
							</button>
						</div>
					</div>
				)}

				{/* Submissions Grid */}
				{filteredSubmissions.length > 0 ? (
					<div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6'>
						{filteredSubmissions.map((submission) => {
							const statusInfo = getStatusInfo(submission.status);
							const previewEntries = getFormDataPreview(submission);

							return (
								<div
									key={submission.id}
									onClick={() => onEdit && onEdit(submission)}
									onKeyDown={(e) => e.key === 'Enter' && onEdit && onEdit(submission)}
									role="button"
									tabIndex={0}
									aria-label={`${submission.isDraft ? 'Continue editing' : 'View'} ${submission.form_name}`}
									className='bg-white rounded-lg shadow-sm border hover:shadow-md hover:border-primary transition-all duration-200 flex flex-col h-full cursor-pointer focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 group'>
									{/* Card Header */}
									<div className='p-5 border-b border-gray-200'>
										<div className='flex items-start justify-between gap-2 min-w-0 flex-wrap'>
											<div className='flex items-start gap-2 min-w-0 flex-1'>
												<FiFileText className='h-5 w-5 text-gray-400 mt-0.5 flex-shrink-0' />
												<h3 className='text-base font-montserrat-semibold text-gray-900 truncate min-w-0'>
													{submission.form_name}
												</h3>
											</div>

											{/* Status Badge */}
											<div className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-montserrat-medium whitespace-nowrap flex-shrink-0 ${statusInfo.bgColor} ${statusInfo.color}`}>
												<statusInfo.icon className='mr-1 h-3 w-3' />
												{statusInfo.label}
											</div>
										</div>
									</div>

									{/* Card Body - Preview */}
									<div className='p-5 flex-1 bg-gray-50/50'>
										{previewEntries.length > 0 ? (
											<div className='space-y-2'>
												<div className='text-xs font-montserrat-medium text-gray-400 uppercase tracking-wider mb-2'>
													Preview
												</div>
												{previewEntries.map((entry, idx) => (
													<div key={idx} className='flex items-start gap-2'>
														<span className='font-montserrat-semibold text-gray-900 text-sm flex-shrink-0'>
															{entry.label}:
														</span>
														<span className='font-montserrat-regular text-gray-500 text-sm line-clamp-1 flex-1'>
															{entry.value}
														</span>
													</div>
												))}
											</div>
										) : (
											<div className='text-sm font-montserrat-regular text-gray-400 italic'>
												No preview available
											</div>
										)}
									</div>

									{/* Card Footer */}
									<div className='px-5 py-4 border-t border-gray-200 flex items-center justify-between'>
										<div className='flex items-center gap-2 text-xs text-gray-500'>
											<FiCalendar className='h-3.5 w-3.5' />
											<span className='font-montserrat-medium'>
												{submission.isDraft ? 'Saved' : 'Submitted'}
											</span>
											<span className='text-gray-400'>•</span>
											<span className='font-montserrat-regular text-gray-600'>
												{formatDate(submission.submitted_at || submission.created_at).split(',')[0]}
											</span>
										</div>
										<FiArrowRight className='h-4 w-4 text-gray-300 group-hover:text-primary group-hover:translate-x-0.5 transition-all' />
									</div>
								</div>
							);
						})}
					</div>
				) : (
					<div className='bg-white rounded-lg shadow-sm border p-12 text-center'>
						<FiFileText className='mx-auto h-12 w-12 text-gray-400 mb-4' />
						<h3 className='text-lg font-montserrat-medium text-gray-900 mb-2'>
							No Submissions Found
						</h3>
						<p className='text-sm font-montserrat-regular text-gray-600 mb-6'>
							{submissions.length === 0 
								? "You haven't submitted any forms yet."
								: "No submissions match your current filters."
							}
						</p>
						<button
							onClick={onBack}
							className='inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-montserrat-medium rounded-md text-white bg-gray-900 hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-900'>
							<FiArrowLeft className='mr-2 h-4 w-4' />
							Back to Forms
						</button>
					</div>
				)}

				{/* Summary */}
				{submissions.length > 0 && (
					<div className='mt-8 text-center'>
						<p className='text-sm font-montserrat-regular text-gray-500'>
							Showing {filteredSubmissions.length} of {submissions.length} submissions
						</p>
					</div>
				)}
			</div>
		</div>
	);
};

export default FormSubmissionHistory;